// src/client/renderer/vfx/items/shadow-stalker.ts
//
// Plan 17, Task 5.1 — Shadow Stalker visual layer (NPC ghost VFX).
//
// Triggered by FIVE events:
//   1. ItemCastEvent (itemId='shadow_stalker') → phase='cast':
//        Dark summon circle at caster (700ms radial sweep, scale 0→1.5, opacity 1→0).
//        Colour: 0x222244 (deep purple-black). Pool: max-active=2.
//
//   2. NPC lifecycle events are owned by the main board Shadow Stalker model in
//        main.ts. This legacy item pool intentionally ignores npc_spawned,
//        npc_moved, npc_killed_player, and npc_despawned so it cannot create
//        duplicate flat rings/ghost sprites over the 3D portal and model.
//
// Pool slots:
//   cast:   one-shot max-active=2 (summon circle activeSet)
//   active: single-instance per npcId Map<npcId, GhostState> (F6 release-first)
//   tick:   NOT a new emitter — tweens existing ghost, returns null
//   impact: NOT a new emitter — tweens existing ghost scale, returns null
//   expire: NOT a new emitter — starts dissipation on ghost, returns null
//
// Audio:
//   Manager fires the item cue only for phase='cast'. NPC follow-up visuals are
//   intentionally silent.
//
// Registry tick contract (HF1):
//   pool.update(_deltaSeconds) IGNORES dt — does NOT call registry.update().
//   The caller (item-vfx-manager at Task 6.1) advances the shared registry once
//   per frame across all pools.
//
// F4 dispose iterates BOTH the summon-circle activeSet AND the ghost Map<npcId,
// GhostState>. F5 acquire-after-dispose returns null. F6 release-first for
// ghost re-summon via same npcId.

import * as THREE from 'three';
import type { GameEvent, ItemCastEvent } from '../../../../shared/game/types';
import type { Npc } from '../../../../shared/game/npc/types';
import type { ItemVfxPool, ItemVfxPoolDeps, ItemVfxSpawnArgs } from './item-vfx-types';
import { disposeSkillshotGroup } from './skillshot-shared';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/** Summon-circle max active slots. */
const MAX_CIRCLES = 2;

/** Ghost-mesh max concurrent (practical max ≈ 1, must tolerate 4 during transient replacement). */
const MAX_GHOSTS = 4;

/** Summon circle lifetime (700ms). */
const CIRCLE_LIFETIME = 0.7;

/** Ghost lunge scale-spike duration (300ms). */
const LUNGE_DURATION = 0.3;

/** Ghost dissipation duration (500ms). */
const DISSIPATE_DURATION = 0.5;

/** Ghost move tween duration per step (200ms). */
const MOVE_DURATION = 0.2;

/** Shadow Stalker deep purple-black colour. */
const STALKER_COLOUR = 0x222244;

// ─────────────────────────────────────────────────────────────────────────────
// Internal state interfaces
// ─────────────────────────────────────────────────────────────────────────────

interface EmitterState {
  handle: ReturnType<ItemVfxPoolDeps['registry']['create']>;
  finished: boolean;
}

interface GhostState {
  handle: ReturnType<ItemVfxPoolDeps['registry']['create']>;
  emitter: THREE.Group;
  /** The ghost sprite for direct access during tweens. */
  ghostSprite: THREE.Sprite;
  /** Ghost material for opacity tweens. */
  material: THREE.SpriteMaterial;
  finished: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Build helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build the summon-circle emitter (cast phase).
 * Dark radial sweep: scale 0→1.5, opacity 1→0 over 700ms.
 */
function buildSummonCircleEmitter(
  anchor: { x: number; y: number; z: number },
  deps: ItemVfxPoolDeps,
  state: EmitterState,
): THREE.Group {
  const { registry } = deps;
  const group = new THREE.Group();

  // Outer ring: flat ring mesh on the ground plane
  const ringGeom = new THREE.RingGeometry(0.3, 0.7, 24);
  const ringMat = new THREE.MeshBasicMaterial({
    color: STALKER_COLOUR,
    transparent: true,
    opacity: 1.0,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const ring = new THREE.Mesh(ringGeom, ringMat);
  ring.position.set(anchor.x, anchor.y, anchor.z);
  ring.rotation.x = Math.PI / 2; // lay flat on ground
  ring.scale.set(0, 0, 0);
  group.add(ring);

  const handle = registry.create('shadow_stalker_cast');
  state.handle = handle;
  const tl = handle.timeline;

  // Radial sweep: scale 0→1.5 over first half of lifetime
  tl.to(
    ring.scale,
    {
      x: 1.5,
      y: 1.5,
      z: 1,
      duration: CIRCLE_LIFETIME * 0.5,
      ease: 'power2.out',
    },
    0,
  );

  // Fade out: opacity 1→0 over entire lifetime (starts with full opacity,
  // fading more noticeably in the second half)
  const opProxy = { value: 1.0 };
  tl.to(
    opProxy,
    {
      value: 0.0,
      duration: CIRCLE_LIFETIME,
      ease: 'power1.in',
      onUpdate: () => {
        ringMat.opacity = opProxy.value;
      },
    },
    0,
  );

  // Mark finished at end of lifetime
  tl.call(
    () => {
      state.finished = true;
    },
    undefined,
    CIRCLE_LIFETIME,
  );

  return group;
}

/**
 * Build the ghost mesh emitter (active phase / npc_summoned).
 * Ethereal ghost sprite at the NPC's spawn node position.
 */
function buildGhostEmitter(
  anchor: { x: number; y: number; z: number },
  deps: ItemVfxPoolDeps,
  state: GhostState,
): THREE.Group {
  const { registry } = deps;
  const group = new THREE.Group();

  const ghostMat = new THREE.SpriteMaterial({
    color: STALKER_COLOUR,
    transparent: true,
    opacity: 0.0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const ghost = new THREE.Sprite(ghostMat);
  ghost.position.set(anchor.x, anchor.y, anchor.z);
  ghost.scale.set(0.5, 0.5, 1);
  group.add(ghost);

  // Store refs for tween access
  state.ghostSprite = ghost;
  state.material = ghostMat;

  const handle = registry.create('shadow_stalker_ghost');
  state.handle = handle;
  const tl = handle.timeline;

  // Spawn fade-in (0 → 300ms): opacity 0→0.8
  const opProxy = { value: 0.0 };
  tl.to(
    opProxy,
    {
      value: 0.8,
      duration: 0.3,
      ease: 'power2.out',
      onUpdate: () => {
        ghostMat.opacity = opProxy.value;
      },
    },
    0,
  );

  // Persistent shimmer pulse (300ms → ∞)
  const shimmerProxy = { opacity: 0.8 };
  tl.to(
    shimmerProxy,
    {
      opacity: 0.4,
      duration: 1.2,
      ease: 'sine.inOut',
      yoyo: true,
      repeat: -1,
      onUpdate: () => {
        ghostMat.opacity = shimmerProxy.opacity;
      },
    },
    0.3,
  );

  return group;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory — createShadowStalkerPool
// ─────────────────────────────────────────────────────────────────────────────

export function createShadowStalkerPool(deps: ItemVfxPoolDeps): ItemVfxPool {
  const { scene } = deps;

  /** Summon-circle one-shot pool (cast phase), max-active=2 */
  const circleStates = new Map<THREE.Object3D, EmitterState>();

  /** Ghost mesh map (active/long-lived), keyed by npcId. */
  const ghostMap = new Map<string, GhostState>();

  let disposed = false;

  // ── acquire ──────────────────────────────────────────────────────────────

  function acquire(args: ItemVfxSpawnArgs): THREE.Object3D | null {
    if (disposed) return null;

    const { phase } = args;
    const { registry } = deps;

    // ── Cast phase: dark summon circle ──────────────────────────────────
    if (phase === 'cast') {
      if (circleStates.size >= MAX_CIRCLES) return null;
      const anchor = args.casterPosition ?? { x: 0, y: 0, z: 0 };
      const state: EmitterState = { handle: null!, finished: false };
      const group = buildSummonCircleEmitter(anchor, deps, state);
      scene.add(group);
      circleStates.set(group, state);
      return group;
    }

    // ── Active phase: ghost spawn at NPC's node ─────────────────────────
    // Triggered by NpcSummonedEvent. npcId is passed via args.casterId.
    if (phase === 'active') {
      const npc = args.npc;
      const npcId = npc?.id ?? args.casterId ?? '';

      if (!npcId) return null;

      // F6 release-first: release any prior ghost for this npcId
      const prior = ghostMap.get(npcId);
      if (prior) {
        prior.handle.dispose();
        scene.remove(prior.emitter);
        disposeSkillshotGroup(prior.emitter);
        ghostMap.delete(npcId);
      }

      // Max ghost guard
      if (ghostMap.size >= MAX_GHOSTS) return null;

      const anchor = args.casterPosition ?? args.targetPosition ?? { x: 0, y: 0, z: 0 };
      const state: GhostState = {
        handle: null!,
        emitter: null!,
        ghostSprite: null!,
        material: null!,
        finished: false,
      };
      const group = buildGhostEmitter(anchor, deps, state);
      state.emitter = group;
      scene.add(group);
      ghostMap.set(npcId, state);
      return group;
    }

    // ── Tick phase: npc_moved — move ghost to new position ──────────────
    // Does NOT create a new emitter — tweens existing ghost. Returns null.
    if (phase === 'tick') {
      const npcId = args.casterId ?? '';
      const existing = ghostMap.get(npcId);
      if (!existing) return null; // No ghost active for this npcId

      const newPos = args.targetPosition;
      if (!newPos) return null;

      // Create a fresh tween handle for the position move
      const moveHandle = registry.create('shadow_stalker_move');
      const moveTl = moveHandle.timeline;

      const sprite = existing.ghostSprite;
      moveTl.to(
        sprite.position,
        {
          x: newPos.x,
          y: newPos.y,
          z: newPos.z,
          duration: MOVE_DURATION,
          ease: 'power1.inOut',
        },
        0,
      );

      // Self-dispose when move completes — prevents handle leak
      moveTl.call(
        () => {
          moveHandle.dispose();
        },
        undefined,
        MOVE_DURATION,
      );

      return null; // No new emitter
    }

    // ── Impact phase: npc_killed_player — ghost lunges at victim ────────
    // Scale spike 1→1.4 then back, 300ms. Does NOT create a new emitter.
    if (phase === 'impact') {
      const npcId = args.casterId ?? '';
      const existing = ghostMap.get(npcId);
      if (!existing) return null; // Race condition — no ghost active

      const sprite = existing.ghostSprite;
      const initialScale = sprite.scale.x;

      const lungeHandle = registry.create('shadow_stalker_lunge');
      const lungeTl = lungeHandle.timeline;

      // Scale: 1→1.4 over first half
      lungeTl.to(
        sprite.scale,
        {
          x: initialScale * 1.4,
          y: initialScale * 1.4,
          z: initialScale * 1.4,
          duration: LUNGE_DURATION * 0.5,
          ease: 'power2.out',
        },
        0,
      );

      // Scale: 1.4→1 over second half
      lungeTl.to(
        sprite.scale,
        {
          x: initialScale,
          y: initialScale,
          z: initialScale,
          duration: LUNGE_DURATION * 0.5,
          ease: 'power1.in',
        },
        LUNGE_DURATION * 0.5,
      );

      // Self-dispose when lunge completes — prevents handle leak
      lungeTl.call(
        () => {
          lungeHandle.dispose();
        },
        undefined,
        LUNGE_DURATION,
      );

      return null; // No new emitter
    }

    // ── Expire phase: npc_despawned — ghost dissipates ──────────────────
    // Opacity 1→0 over 500ms. npcId removed from ghostMap after animation.
    if (phase === 'expire') {
      const npcId = args.casterId ?? '';
      const existing = ghostMap.get(npcId);
      if (!existing) return null; // No ghost active — no-op

      // Kill the existing shimmer handle
      existing.handle.dispose();

      // Create fresh dissipation handle
      const dissipateHandle = registry.create('shadow_stalker_dissipate');
      existing.handle = dissipateHandle;
      const tl = dissipateHandle.timeline;

      const mat = existing.material;
      const emitter = existing.emitter;

      const opProxy = { value: mat.opacity };
      tl.to(
        opProxy,
        {
          value: 0.0,
          duration: DISSIPATE_DURATION,
          ease: 'power1.in',
          onUpdate: () => {
            mat.opacity = opProxy.value;
          },
        },
        0,
      );

      // Clean up after dissipation completes
      tl.call(
        () => {
          ghostMap.delete(npcId);
          scene.remove(emitter);
          disposeSkillshotGroup(emitter);
          dissipateHandle.dispose();
        },
        undefined,
        DISSIPATE_DURATION,
      );

      return null; // No new emitter
    }

    return null;
  }

  // ── update ───────────────────────────────────────────────────────────────
  // Registry tick contract (HF1): pool.update does NOT advance the registry.

  function update(_deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    const all = new Set<THREE.Object3D>();
    for (const emitter of circleStates.keys()) all.add(emitter);
    for (const { emitter } of ghostMap.values()) all.add(emitter);
    return all;
  }

  // ── isFinished ───────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    const circleState = circleStates.get(emitter);
    if (circleState) return circleState.finished;

    for (const state of ghostMap.values()) {
      if (state.emitter === emitter) return state.finished;
    }

    // Unknown emitter
    return true;
  }

  // ── release ──────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    // Check summon-circle pool
    const circleState = circleStates.get(emitter);
    if (circleState) {
      circleState.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
      circleStates.delete(emitter);
      return;
    }

    // Check ghost map
    for (const [npcId, state] of ghostMap.entries()) {
      if (state.emitter === emitter) {
        state.handle.dispose();
        scene.remove(emitter);
        disposeSkillshotGroup(emitter);
        ghostMap.delete(npcId);
        return;
      }
    }
  }

  // ── dispose (F4) ─────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Dispose summon-circle active set
    for (const [emitter, state] of circleStates) {
      state.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
    }
    circleStates.clear();

    // Dispose ghost map
    for (const state of ghostMap.values()) {
      state.handle.dispose();
      scene.remove(state.emitter);
      disposeSkillshotGroup(state.emitter);
    }
    ghostMap.clear();
  }

  return { acquire, update, isFinished, release, dispose };
}

// ─────────────────────────────────────────────────────────────────────────────
// Event resolver — resolveShadowStalkerForEvent
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolver for Shadow Stalker VFX events.
 *
 * Only item_cast resolves here. NPC lifecycle visuals use the board-owned 3D
 * Shadow Stalker model so the portal, movement, kill, and despawn animations
 * stay in one system.
 *
 * @param event          - The game event to resolve.
 * @param getPositionForPlayer - Optional callback: playerId → world position.
 * @param getPositionForNode   - Optional callback: nodeId → world position.
 * @param getNpcById           - Optional callback: npcId → Npc | undefined.
 *   Signature compatible with `findNpcById` at src/shared/game/npc/registry.ts:8.
 */
export function resolveShadowStalkerForEvent(
  event: GameEvent,
  getPositionForPlayer?: (playerId: string) => { x: number; y: number; z: number } | undefined,
  _getPositionForNode?: (nodeId: string) => { x: number; y: number; z: number } | undefined,
  _getNpcById?: (npcId: string) => Npc | undefined,
): { poolId: 'shadow_stalker'; args: ItemVfxSpawnArgs } | null {
  // ── item_cast with itemId='shadow_stalker' → summon circle ───────────────
  if (event.type === 'item_cast') {
    const cast = event as ItemCastEvent;
    if (cast.itemId !== 'shadow_stalker') return null;
    return {
      poolId: 'shadow_stalker',
      args: {
        phase: 'cast',
        casterId: cast.casterId,
        casterPosition: getPositionForPlayer?.(cast.casterId),
      },
    };
  }

  return null;
}
